// ============================================================
// NumberInput - a controlled numeric <input> that can actually be
// cleared while editing.
//
// The app-wide footgun it fixes: `value={someNumber}` +
// `onChange={parseInt(e.target.value) || 0}` snaps an emptied field
// back to 0/1, so the last digit can never be deleted. This keeps a
// local string while focused (empty and lone "-" allowed, no emit),
// emits the clamped number on every valid keystroke, and commits a
// clamped fallback on blur. When not focused it mirrors the prop, so
// +/- spinner arrows, spawn, and reset still update the field.
// ============================================================
import { useEffect, useRef, useState } from 'react';
import type { InputHTMLAttributes, Ref } from 'react';
import { clampNumber, committedNumber, isPartialNumber, parseNumber } from '../utils/numberField';

type NativeProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'min' | 'max' | 'step' | 'type'>;

interface NumberInputProps extends NativeProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Allow negative values (default: floor at min ?? 0). */
  allowNegative?: boolean;
  /**
   * Accept fractions.
   *
   * OPT-IN, because every caller written before structures existed wants
   * whole numbers - cubes, storeys, sides - and `parseInt` is what makes
   * "3.9 cubes" impossible to type by accident. A pillar's radius is not
   * a count, so it needs the other behaviour (v8.0.0).
   */
  float?: boolean;
  /**
   * Show a change that came from OUTSIDE even while the field has focus -
   * for a field with its own steppers beside it.
   *
   * OPT-IN, because it compares the prop against what the field last
   * emitted: an owner that applies edits late (a debounce) would hand
   * back an older value mid-typing and retype the field under the user.
   */
  mirrorWhileFocused?: boolean;
  ref?: Ref<HTMLInputElement>;
}

/**
 * The field's text, kept apart from its value while it is being edited.
 *
 * Mirrors external changes (spinner, spawn, reset) only while not
 * editing - or, when asked, while editing too, but never the field's own
 * echo, which must not retype what is being typed.
 */
function useFieldText(value: number, onChange: (value: number) => void, mirrorWhileFocused: boolean) {
  const [text, setText] = useState<string>(String(value));
  const focused = useRef(false);
  // What this field last told its owner: a prop that differs from it was
  // changed by somebody else (see `mirrorWhileFocused`).
  const emitted = useRef(value);
  useEffect(() => {
    const external = mirrorWhileFocused && value !== emitted.current;
    if (!focused.current || external) setText(String(value));
    emitted.current = value;
  }, [value, mirrorWhileFocused]);
  const emit = (n: number) => {
    emitted.current = n;
    onChange(n);
  };
  return { text, setText, focused, emit };
}

export default function NumberInput({
  value,
  onChange,
  min,
  max,
  step,
  allowNegative = false,
  float = false,
  mirrorWhileFocused = false,
  onFocus,
  onBlur,
  ...rest
}: NumberInputProps) {
  const bounds = { min, max, allowNegative, float };
  const { text, setText, focused, emit } = useFieldText(value, onChange, mirrorWhileFocused);

  return (
    <input
      {...rest}
      type="number"
      min={min}
      max={max}
      step={step}
      value={text}
      onFocus={(e) => {
        focused.current = true;
        onFocus?.(e);
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        if (isPartialNumber(raw, float)) return;
        const n = parseNumber(raw, float);
        if (Number.isFinite(n)) emit(clampNumber(n, bounds));
      }}
      onBlur={(e) => {
        focused.current = false;
        const committed = committedNumber(text, bounds);
        setText(String(committed));
        emit(committed);
        onBlur?.(e);
      }}
    />
  );
}
